import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useEventHistory } from "../api/events-api";
import { useFinanceTransactions } from "../api/finance-api";
import { useLogeMembers } from "../api/user-api";
import { Icon } from "../components/dss/Icon";
import { TransactionRow } from "../components/dss/finance/TransactionRow";
import { VaultSkeleton } from "../components/dss/finance/VaultSkeleton";
import { Stagger, StaggerItem, useEnterAnimation } from "../components/dss/Motion";
import { formatKr, groupByYear } from "../finance/ledger";
import { useSupabaseAuth } from "../supabase/SupabaseContext";

/**
 * The whole ledger, a section per year.
 *
 * This used to be the movements list on the finance screen expanding in
 * place. Two reasons it isn't any more. It made the chart screen grow a
 * ledger's worth of rows under a chart that is meant to be the point of it —
 * and the light on that screen is painted by one element sized to the
 * content, so growing the content mid-tap moved the gradient under
 * everything that was already on screen. A list this long is a place you go,
 * not a thing a screen turns into.
 *
 * Sections aren't sticky. A sticky header over a gradient is an opaque
 * rectangle sliding across it — the same seam problem as the safe areas, in
 * the middle of the screen this time. With a loge's worth of movements a
 * year the headers are never far off anyway; if the ledger ever gets long
 * enough to want them pinned, they want a frosted backdrop rather than a
 * flat fill.
 */
const TransactionsPage: FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useSupabaseAuth();
  const { data: transactions = [], isLoading } = useFinanceTransactions();
  const { data: events = [] } = useEventHistory();
  const { data: members = [] } = useLogeMembers();

  const animateIn = useEnterAnimation(isLoading);
  const isAdmin = hasRole("ADMIN");

  const groups = useMemo(() => groupByYear(transactions), [transactions]);
  const eventsById = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);
  const membersById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  return (
    <div
      data-scroll-container
      data-sticky-scroll
      className="dss-vault flex-1 overflow-y-auto text-vault-ink"
    >
      <div className="px-7 pt-2 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate("/finance")}
          className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-vault-ink3 flex items-center gap-1.5"
        >
          <Icon name="chevron-left" size={12} />
          <span>Finans</span>
        </button>
        {isAdmin && (
          <button
            onClick={() => navigate("/finance/add")}
            aria-label="Ny postering"
            className="-m-1.5 p-1.5 text-vault-ink3"
          >
            <Icon name="plus" size={17} />
          </button>
        )}
      </div>

      <div className="px-7 font-display text-[44px] leading-[0.9] tracking-tight font-extrabold mt-3 uppercase">
        Bevægelser<span className="text-mint">.</span>
      </div>
      <div className="px-7 font-mono text-[10px] tracking-[1.4px] uppercase text-vault-ink3 mt-2.5">
        {isLoading
          ? "Henter…"
          : `${transactions.length} ${transactions.length === 1 ? "postering" : "posteringer"}`}
      </div>

      {isLoading ? (
        <div className="px-7 mt-6 flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <VaultSkeleton key={i} className="h-[46px] rounded-lg" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="px-7 mt-6">
          <div className="dss-vault-card rounded-lg px-4 py-8 text-center font-mono text-[10px] tracking-[1.3px] uppercase text-vault-ink3">
            Intet bogført endnu
          </div>
        </div>
      ) : (
        <Stagger enabled={animateIn} className="mt-5 pb-6">
          {groups.map((group) => (
            <StaggerItem key={group.year} className="px-7 mb-5 last:mb-0">
              {/* The year, and what it did. The two figures under it are the
                  sheet's own bottom line: what moved, and what was left. */}
              <div className="flex items-end justify-between gap-3 mb-2.5">
                <div className="font-display text-[26px] leading-none font-extrabold tracking-tight">
                  {group.year}
                </div>
                <div className="text-right">
                  {/* A year that only holds the opening balance has no in and
                      out to report, and "+0,00 / +0,00" is a worse answer
                      than not asking. */}
                  {(group.incomeOre > 0 || group.expenseOre > 0) && (
                    <div className="font-mono text-[10.5px] tabular-nums">
                      <span className="text-mint">
                        {formatKr(group.incomeOre, { signed: true, prefix: false })}
                      </span>
                      <span className="text-vault-ink3 mx-1.5">/</span>
                      <span className="text-ember">
                        {formatKr(-group.expenseOre, { signed: true, prefix: false })}
                      </span>
                    </div>
                  )}
                  <div className="font-mono text-[9px] tracking-[1.2px] uppercase text-vault-ink3 mt-1 tabular-nums">
                    Ultimo {formatKr(group.closingBalanceOre)}
                  </div>
                </div>
              </div>

              <div className="dss-vault-card rounded-lg overflow-hidden divide-y divide-white/[0.06]">
                {group.entries.map(({ transaction, balanceOre }) => (
                  <TransactionRow
                    key={transaction.id}
                    transaction={transaction}
                    event={
                      transaction.loge_event_id
                        ? eventsById.get(transaction.loge_event_id)
                        : undefined
                    }
                    member={
                      transaction.loge_member_id
                        ? membersById.get(transaction.loge_member_id)
                        : undefined
                    }
                    // The opening balance *is* the balance, so printing it
                    // twice on the same row says nothing and costs the note
                    // the width it needs.
                    balanceOre={transaction.category === "primo" ? undefined : balanceOre}
                    onClick={isAdmin ? () => navigate(`/finance/${transaction.id}`) : undefined}
                  />
                ))}
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      )}
    </div>
  );
};

export default TransactionsPage;
